/**
 * Game State Store
 * Zustand store for managing poker game state with optimistic UI updates
 */

import { create } from 'zustand';
import { GameStore, GameState, Player, ActionHistoryItem } from './types';

const initialState: GameState = {
  tableId: null,
  players: [],
  communityCards: Array.from({ length: 9 }, (_, i) => ({
    position: i + 1,
    card: null,
    revealed: false,
  })),
  pot: 0,
  sidePots: [],
  currentBet: 0,
  minRaise: null,
  phase: 'ANTE',
  yourSeat: null,
  yourChips: 0,
  turnPlayerId: null,
  turnDeadline: null,
  dealDeadline: null,
  tableOver: null,
  ante: null,
  knockedOutPlace: null,
  spectating: false,
  awaitingEndState: false,
  roundStartedAt: 0,
  specialCardEffects: false,
  possibleHands: [],
  chatMessages: [],
  isConnected: false,
  // Not 'disconnected': a game page starts by connecting, not having lost it.
  connectionStatus: 'connecting',
  reconnectAttempt: 0,
  handResult: null,
  actionHistory: [],
  pendingAction: null,
  optimisticSnapshot: null,
};

export const useGameStore = create<GameStore>((set) => ({
  ...initialState,

  setGameState: (state) =>
    set((prev) => {
      // state can be a plain partial-state object, or (Zustand-`set`-style)
      // an updater function computed from the previous state — spreading a
      // function directly here silently contributed nothing (functions have
      // no relevant enumerable own properties), so callers using the
      // updater form were actually no-ops at runtime, not just a type error.
      const updates = typeof state === 'function' ? state(prev) : state;
      // A new betting round (or a new hand) starts whenever the phase changes.
      const newRound = updates.phase !== undefined && updates.phase !== prev.phase;
      return {
      ...prev,
      ...updates,
      ...(newRound ? { roundStartedAt: Date.now() } : {}),
      };
    }),

  updatePlayer: (playerId, updates) =>
    set((state) => ({
      players: state.players.map((player) =>
        player.id === playerId ? { ...player, ...updates } : player
      ),
    })),

  updateCommunityCard: (position, card) =>
    set((state) => ({
      communityCards: state.communityCards.map((c) =>
        c.position === position ? card : c
      ),
    })),

  addChatMessage: (message) =>
    set((state) => ({
      chatMessages: [...state.chatMessages.slice(-99), message],
    })),

  setHandResult: (result) =>
    set({ handResult: result }),

  addActionHistory: (action) =>
    set((state) => ({
      // Enough for any betting round in full (the ticker shows the current
      // round's, and dropping one mid-round would shift the tape).
      actionHistory: [...state.actionHistory.slice(-59), action],
    })),

  /**
   * Apply optimistic action update immediately (before server confirmation)
   * Saves current state snapshot for potential rollback
   */
  applyOptimisticAction: (userId, action, amount) =>
    set((state) => {
      // Save snapshot for rollback
      const snapshot = {
        players: [...state.players],
        pot: state.pot,
        currentBet: state.currentBet,
        yourChips: state.yourChips,
        actionHistory: [...state.actionHistory],
      };

      // Find player
      const player = state.players.find((p) => p.id === userId);
      if (!player) return state;

      // Calculate optimistic updates
      const updatedPlayers = state.players.map((p) => {
        if (p.id !== userId) return p;

        const updates: Partial<Player> = {};

        switch (action) {
          case 'FOLD':
            updates.status = 'FOLDED';
            break;
          case 'CHECK':
            // No chip changes
            break;
          case 'CALL': {
            // A call bigger than the stack is an all-in for what's left.
            const paid = Math.min(p.chipCount, state.currentBet - (p.currentBet || 0));
            updates.currentBet = (p.currentBet || 0) + paid;
            updates.chipCount = p.chipCount - paid;
            if (updates.chipCount === 0) updates.status = 'ALL_IN';
            break;
          }
          case 'RAISE':
          case 'BET':
            // Same meaning as the engine: BET is the new bet level, RAISE is
            // how much goes on top of calling the current bet.
            if (amount !== undefined) {
              const target = action === 'BET' ? amount : state.currentBet + amount;
              updates.currentBet = target;
              updates.chipCount = p.chipCount - (target - (p.currentBet || 0));
              if (updates.chipCount === 0) updates.status = 'ALL_IN';
            }
            break;
          case 'ALL_IN':
            updates.status = 'ALL_IN';
            updates.currentBet = (p.currentBet || 0) + p.chipCount;
            updates.chipCount = 0;
            break;
        }

        return { ...p, ...updates };
      });

      // Add optimistic action to history
      const optimisticHistoryItem: ActionHistoryItem = {
        id: `optimistic-${userId}-${Date.now()}`,
        userId,
        username: player.username,
        action,
        amount,
        timestamp: Date.now(),
        isOptimistic: true,
      };

      return {
        players: updatedPlayers,
        actionHistory: [...state.actionHistory, optimisticHistoryItem],
        pendingAction: { userId, action, amount, timestamp: Date.now() },
        optimisticSnapshot: snapshot,
        yourChips: userId === player.id ? updatedPlayers.find((p) => p.id === userId)?.chipCount || state.yourChips : state.yourChips,
      };
    }),

  /**
   * Confirm optimistic action (called when server confirms)
   * Clears pending state and removes optimistic flag from history
   */
  confirmOptimisticAction: () =>
    set((state) => {
      if (!state.pendingAction) return state;

      return {
        pendingAction: null,
        optimisticSnapshot: null,
        actionHistory: state.actionHistory.map((item) =>
          item.isOptimistic ? { ...item, isOptimistic: false } : item
        ),
      };
    }),

  /**
   * Rollback optimistic action (called on error/timeout)
   * Restores state from snapshot
   */
  rollbackOptimisticAction: () =>
    set((state) => {
      if (!state.optimisticSnapshot) return state;

      return {
        players: state.optimisticSnapshot.players,
        pot: state.optimisticSnapshot.pot,
        currentBet: state.optimisticSnapshot.currentBet,
        yourChips: state.optimisticSnapshot.yourChips,
        actionHistory: state.optimisticSnapshot.actionHistory,
        pendingAction: null,
        optimisticSnapshot: null,
      };
    }),

  reset: () => set(initialState),
}));
